import React, {
  useState, useEffect, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GTMTracking } from '@common/ui/general';
import {
  requestParamsSelector,
} from '@alumni_v2/selectors/reviews';
import {
  updateRequestParams,
} from '@alumni_v2/stores/reviews/actions';
import { useMediaQuery } from '@common/hooks';

import filterIcon from '@alumni_v2/images/svg/filterIcon.svg';
import yellowDot from '@alumni_v2/images/svg/yellowDot.svg';

import styles from './AdvanceFilter.module.scss';

const RANGE_FILTER = ['growth', 'experience'];

const AdvanceFilter = ({ filterOptions }) => {
  const dispatch = useDispatch();

  const defaultFilter = 'branch';
  const [isPopUpVisible, setPopUpVisible] = useState(false);
  const [isFilterSelected, setFilterSelected] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState(defaultFilter);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const { mobile } = useMediaQuery();

  useEffect(() => {
    setSelectedFilters({ [defaultFilter]: [] });
  }, []);

  useEffect(() => {
    setFilterSelected(Object.keys(selectedFilters).length > 0);
  }, [selectedFilters]);

  const togglePopUp = useCallback(() => {
    setPopUpVisible((prevState) => !prevState);
  }, []);

  const formatFilterType = (filterType) => filterType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const handleFilterChange = useCallback((filterType, value) => {
    setSelectedFilters((prevSelectedFilters) => ({
      ...prevSelectedFilters,
      // eslint-disable-next-line no-nested-ternary
      [filterType]: prevSelectedFilters[filterType]
        ? prevSelectedFilters[filterType].includes(value)
          ? prevSelectedFilters[filterType].filter((val) => val !== value)
          : [...prevSelectedFilters[filterType], value]
        : [value],
    }));
  }, []);

  const handleFilterTypeSelect = useCallback((filterType) => {
    setSelectedFilterType(filterType);
    setSelectedFilters((prevSelectedFilters) => ({
      ...prevSelectedFilters,
      [filterType]: prevSelectedFilters[filterType] || [],
    }));
  }, []);

  const handleApplyFilter = useCallback(() => {
    setFilteredData(selectedFilters);
    togglePopUp();
    const query = {};
    Object.keys(selectedFilters).forEach((key) => {
      let values = [];
      if (RANGE_FILTER.includes(key)) {
        selectedFilters[key].forEach((filter) => {
          let [gt, lt] = filter.split('-').map(item => item.trim());
          if (!lt) {
            gt = gt.split('and')[0].trim();
            lt = null;
            values.push({
              min: gt,
            });
          } else {
            values.push({
              max: lt,
              min: gt,
            });
          }
        });
        query[key] = values;
      } else {
        values = selectedFilters[key];
        query[`${key}_matches_any`] = values;
      }
    });
    dispatch(
      updateRequestParams(
        [], query, null,
      ),
    );
  }, [dispatch, selectedFilters, togglePopUp]);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({});
    setFilteredData({});
    setSelectedFilterType(defaultFilter);
    togglePopUp();
    dispatch(
      updateRequestParams(
        [], {}, null,
      ),
    );
  }, [dispatch, defaultFilter, togglePopUp]);

  const requestParams = useSelector(requestParamsSelector) || {};

  useEffect(() => {
    if (
      requestParams.search
        || requestParams.quickFilters.length
        || Object.keys(requestParams.advancedFilters).length === 0
    ) {
      setSelectedFilters({});
      setFilteredData({});
      setSelectedFilterType(defaultFilter);
    }
  }, [requestParams, handleClearFilters]);

  return (
    <div className={styles.container}>
      <div className={styles.filterButtonContainer}>
        {mobile ? (
          <GTMTracking
            className={
              `${styles.filterButton}
            ${isPopUpVisible ? styles.filterButtonSelected : ''}
            ${(Object.keys(filteredData).length !== 0) && styles.appliedFilter}`
            }
            component="button"
            type="button"
            onClick={togglePopUp}
            gtmEventType="cta"
            gtmEventAction="click"
            gtmEventResult="advanced_filters"
            gtmEventCategory="alumni_directory"
            gtmEventData={{
              click_type: 'advanced_filters',
            }}
          >
            <div className={styles.filterredIcon}>
              <img src={filterIcon} alt="filterIcon" />
              {(Object.keys(filteredData).length !== 0)
              && <img className={styles.yellowDot} src={yellowDot} alt="" />}
            </div>
          </GTMTracking>
        ) : (
          <GTMTracking
            className={
              `${styles.filterButton}
            ${isPopUpVisible ? styles.filterButtonSelected : ''}
            ${(Object.keys(filteredData).length !== 0) && styles.appliedFilter}`
            }
            component="button"
            type="button"
            onClick={togglePopUp}
            gtmEventType="cta"
            gtmEventAction="click"
            gtmEventResult="advanced_filters"
            gtmEventCategory="alumni_directory"
            gtmEventData={{
              click_type: 'advanced_filters',
            }}
          >
            <div className={styles.filterredIcon}>
              <img src={filterIcon} alt="filterIcon" />
              {(Object.keys(filteredData).length !== 0)
              && <img className={styles.yellowDot} src={yellowDot} alt="" />}
            </div>
            <div>Advanced Filters</div>
          </GTMTracking>
        )}
      </div>

      {isPopUpVisible && (
        <div className={styles.advanceFilterPopUp}>
          <div className={styles.topSection}>
            <div className={styles.heading}>Filters</div>
            <GTMTracking
              component="button"
              className={`${styles.clearFilterBtn} ${
                isFilterSelected ? styles.toggleClearFilterBtn : ''
              }`}
              onClick={handleClearFilters}
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult="clear_all_advanced_filters"
              gtmEventCategory="alumni_directory"
              gtmEventData={{
                click_type: 'clear_all_advanced_filters',
              }}
            >
              Clear all filters
            </GTMTracking>
          </div>

          <div className={styles.filterItemSection}>
            <div className={styles.leftContainer}>
              {Object.keys(filterOptions).map((filterType) => (
                <GTMTracking
                  component="div"
                  key={filterType}
                  className={
                    `${styles.filterName} 
                    ${selectedFilterType === filterType ? styles.selected : ''}`
                  }
                  onClick={() => handleFilterTypeSelect(filterType)}
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="advanced_filter_category"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_type: 'advanced_filter_category',
                    click_text: formatFilterType(filterType),
                  }}
                >
                  {formatFilterType(filterType)}
                </GTMTracking>
              ))}
            </div>
            <div className={styles.rightContainer}>
              <div
                className={styles.selectedFilterType}
                key={selectedFilterType}
              >
                {filterOptions[selectedFilterType].map((value) => (
                  <div
                    key={value}
                  >
                    <label className={styles.optionLabels} key={value}>
                      <GTMTracking
                        component="input"
                        gtmEventType="cta"
                        gtmEventAction="click"
                        gtmEventResult="advanced_filter_item"
                        gtmEventCategory="alumni_directory"
                        gtmEventData={{
                          click_type: 'advanced_filter_item',
                          click_text: value,
                        }}
                        type="checkbox"
                        value={value}
                        checked={
                          !!selectedFilters[selectedFilterType]?.includes(value)
                        }
                        onChange={
                          () => handleFilterChange(selectedFilterType, value)
                        }
                      />
                      {value}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <GTMTracking
              className={styles.closeBtn}
              onClick={togglePopUp}
              component="button"
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult="close_advanced_filters"
              gtmEventCategory="alumni_directory"
              gtmEventData={{
                click_type: 'close_advanced_filters',
              }}
            >
              Close
            </GTMTracking>
            <GTMTracking
              className={styles.applyBtn}
              onClick={handleApplyFilter}
              component="div"
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult="apply_advanced_filters"
              gtmEventCategory="alumni_directory"
              gtmEventData={{
                click_type: 'apply_advanced_filters',
              }}
            >
              Apply Filters
            </GTMTracking>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvanceFilter;
